import {AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {FormArray, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {FacilityService} from '../../../shared/services/facility.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {Uuid} from '../../../shared/interface/response/uuid';
import {PartnerFacility} from '../../../shared/interface/partner-facility';
import {forkJoin, Observable, of, ReplaySubject} from 'rxjs';
import {Router} from '@angular/router';
import {catchError, delay, first, map, mapTo, switchMap, take, takeUntil, tap} from 'rxjs/operators';
import {FormHelperService} from '../../../shared/services/form-helper.service';
import {PartnerService} from '../../../shared/services/partner.service';
import {Partner} from '../../../shared/interface/partner';
import {Dictionary} from '../../../shared/interface/dictionary';
import {ArrayValidators} from '../../../shared/validator/array-validators';
import {HttpErrorResponse} from '@angular/common/http';
import {
  PARTNER_TAGS_GOOD_AGENT,
  FACILITY_TYPE_OWN_ASF_PREMIUM,
  FACILITY_TYPE_OWN_ASF,
  FACILITY_TYPE_SELF_AGENT,
  FACILITY_TYPE_WITH_ASF_MARKETING,
  FACILITY_TYPE_SELF_GOOD_AGENT,
  PARTNER_TAGS_PROFIT,
  DictService
} from '../../../shared/services/dict.service';
import {AuthService} from "../../../shared/services/auth.service";
import {UserApplicationPermission, UserPermissions} from "../../../shared/interface/user-permissions";
import {FacilityDobryAgentService} from "../../../shared/services/facility-dobry-agent.service";
import {FacilityDobryAgent} from "../../../shared/interface/facility-dobry-agent";
import {AngularEditorConfig} from "@kolkov/angular-editor";

@Component({
  selector: 'app-facility-add',
  templateUrl: './facility-add.component.html',
  styleUrls: ['./facility-add.component.scss']
})
export class FacilityAddComponent implements OnInit, AfterViewInit, OnDestroy {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  @Input() partnerFacility?: Observable<PartnerFacility>;
  @Output() closeModification = new EventEmitter<any>();

  readonly PARTNER_TYPES = [
    FACILITY_TYPE_OWN_ASF_PREMIUM,
    FACILITY_TYPE_OWN_ASF,
    FACILITY_TYPE_SELF_AGENT,
    FACILITY_TYPE_WITH_ASF_MARKETING,
    FACILITY_TYPE_SELF_GOOD_AGENT,
  ];

  partnerFacilityObject?: PartnerFacility = null;
  partnerFacilityForm: FormGroup;
  addressForm: FormGroup;
  terminalNumberControl: FormControl;
  partnersFormArray: FormArray;
  noteControl: FormControl;
  assistantDirectorControl: FormControl;
  regionalControl: FormControl;
  structureDirectorControl: FormControl;
  facilityTypeControl: FormControl;
  facilityDobryAgentControl: FormControl;
  addFacilityDobryAgentControl: FormControl;
  facilityDobryAgentNameControl: FormControl;
  facilityDobryAgentDescriptionControl: FormControl;
  facilityDobryAgentCityControl: FormControl;
  processingRequest = false;
  profitFacility = false;
  dobryAgentFacility = false;
  profitPartner = false;
  profitTag = false;
  errors: any = {};
  init = false;
  userPermissions: ReplaySubject<UserPermissions>;
  userApplicationPermission: ReplaySubject<UserApplicationPermission>;
  linkedDobryAgentFacility: FacilityDobryAgent | null = null;
  checkingDobryAgentLink = false;

  editorConfig: AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    height: '220px',
    minHeight: '160px',
    maxHeight: '400px',
    width: '100%',
    minWidth: '0',
    translate: 'yes',
    enableToolbar: true,
    showToolbar: true,
    placeholder: 'Wpisz opis placówki...',
    defaultParagraphSeparator: 'p',
    defaultFontName: 'Arial',
    sanitize: false,
    toolbarHiddenButtons: [
      [
        'strikeThrough',
        'subscript',
        'superscript',
        'justifyRight',
        'justifyFull',
        'indent',
        'outdent',
        'insertUnorderedList',
        'insertOrderedList',
        'heading',
        'fontName',
        'fontSize',
        'textColor',
        'backgroundColor',
        'customClasses',
        'link',
        'unlink',
        'insertImage',
        'insertVideo',
      ],
      [
        // 'toggleEditorMode'
      ]
    ]
  };

  constructor(
    private facilityService: FacilityService,
    private snackBar: MatSnackBar,
    private router: Router,
    private fb: FormBuilder,
    private formHelper: FormHelperService,
    private partnerService: PartnerService,
    private auth: AuthService,
    private facilityDobryAgentService: FacilityDobryAgentService,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.userApplicationPermission = auth.getUserApplicationPermission();

    this.partnerFacilityForm = this.fb.group({
      address: this.addressForm = this.formHelper.generateAddressFormGroup(),
      partners: this.partnersFormArray = fb.array([], [ArrayValidators.minLength(1)]),
      note: this.noteControl = fb.control(''),
      assistantDirector: this.assistantDirectorControl = fb.control(''),
      regional: this.regionalControl = fb.control(''),
      structureDirector: this.structureDirectorControl = fb.control(''),
      facilityType: this.facilityTypeControl = fb.control('', [Validators.required]),
      terminalNumber: this.terminalNumberControl = fb.control(''),
    });

    this.facilityDobryAgentControl = this.fb.control('');
    this.addFacilityDobryAgentControl = fb.control(false);
    this.facilityDobryAgentNameControl = this.fb.control('');
    this.facilityDobryAgentDescriptionControl = this.fb.control('');
    this.facilityDobryAgentCityControl = this.fb.control('');
  }

  ngOnInit(): void {
    this.partnerFacilityForm.get('partners').valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((partnerIds: string[]) => {
        if (!this.init) {
          this.profitFacility = false;
          this.dobryAgentFacility = false;
          this.profitPartner = false;
          this.profitTag = false;
          partnerIds.forEach((partnerId: string) => {
            if (!partnerId) {
              return;
            }
            this.partnerService.get(partnerId)
              .subscribe((partner: Partner) => {
                this.profitPartner = true;
                partner.tags.forEach((tag: Dictionary) => {
                  if (tag.key === PARTNER_TAGS_GOOD_AGENT) {
                    this.profitTag = true;
                    this.profitFacility = false;
                    this.profitPartner = false;
                    this.dobryAgentFacility = true;
                    return;
                  }
                  if (tag.key === PARTNER_TAGS_PROFIT) {
                    this.profitFacility = true;
                    return;
                  }
                });
              });
          });
        }
      });
    this.addFacilityDobryAgentControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((checked: boolean) => {
        if (checked) {
          this.facilityDobryAgentControl.setValue('');
          this.facilityDobryAgentNameControl.setValidators([Validators.required]);
          this.facilityDobryAgentDescriptionControl.setValidators([Validators.required]);
        } else {
          this.facilityDobryAgentNameControl.clearValidators();
          this.facilityDobryAgentDescriptionControl.clearValidators();
          this.facilityDobryAgentNameControl.setValue('');
          this.facilityDobryAgentDescriptionControl.setValue('');
        }

        this.facilityDobryAgentNameControl.updateValueAndValidity();
        this.facilityDobryAgentDescriptionControl.updateValueAndValidity();
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  ngAfterViewInit(): void {
    if (this.partnerFacility) {
      this.partnerFacility
        .pipe(
          first(),
          delay(0),
          tap((partnerFacility: PartnerFacility) => {
            if (partnerFacility === null) {
              return;
            }
            this.init = true;
            this.partnerFacilityObject = partnerFacility;
            this.addressForm.reset({
              street: partnerFacility.address.street,
              houseNumber: partnerFacility.address.houseNumber,
              apartmentNumber: partnerFacility.address.apartmentNumber,
              city: partnerFacility.address.city,
              zipCode: partnerFacility.address.zipCode,
              state: partnerFacility.address.state?.id,
              postCity: partnerFacility.address.postCity,
              district: partnerFacility.address.district,
              commune: partnerFacility.address.commune,
            });
            this.terminalNumberControl.setValue(partnerFacility.terminalNumber);
            this.facilityTypeControl.setValue(this.partnerFacilityObject.facilityType?.id);
            this.partnerFacilityObject.partners.forEach((partner: Partner) => {
              this.addPartner(partner);
            });

            this.profitFacility = false;
            this.dobryAgentFacility = false;
            this.profitPartner = false;
            this.profitTag = false;
            this.partnerFacilityObject.partners.forEach((partner: Partner) => {
              if (!partner) {
                return;
              }
              this.profitPartner = true;
              partner.tags.forEach((tag: Dictionary) => {
                if (tag.key === PARTNER_TAGS_GOOD_AGENT) {
                  this.profitTag = true;
                  this.profitPartner = false;
                  this.dobryAgentFacility = true;
                  this.profitFacility = false;
                  return;
                }
                if (tag.key === PARTNER_TAGS_PROFIT) {
                  this.profitFacility = true;
                  return;
                }
              });
            });

            this.assistantDirectorControl.setValue(this.partnerFacilityObject.assistantDirector?.uuid);
            this.regionalControl.setValue(this.partnerFacilityObject.regional?.uuid);
            this.structureDirectorControl.setValue(this.partnerFacilityObject.structureDirector?.uuid);
            this.init = false;
          }),
          delay(0),
          tap(() => this.partnerFacilityForm.markAllAsTouched()),
          tap(() => this.loadAndSelectDobryAgentFacility())
        )
        .subscribe();
    } else {
      this.addPartner();
    }
  }

  private loadAndSelectDobryAgentFacility(): void {
    if (!this.partnerFacilityObject?.address?.uuid) {
      return;
    }

    if (!this.hasPartnerTag(this.partnerFacilityObject, PARTNER_TAGS_GOOD_AGENT)) {
      this.linkedDobryAgentFacility = null;
      this.facilityDobryAgentControl.setValue('');
      return;
    }

    this.checkingDobryAgentLink = true;

    this.userPermissions
      .pipe(
        take(1),
        switchMap((user: UserPermissions) => {
          if (!user?.facility?.admin_root) {
            return of(null);
          }

          return this.facilityDobryAgentService.getList({
            disableCount: true,
          }).pipe(take(1));
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (res: any) => {
          this.checkingDobryAgentLink = false;

          if (!res?.items?.length) {
            this.linkedDobryAgentFacility = null;
            this.facilityDobryAgentControl.setValue('');
            return;
          }

          const asf24AddressUuid = this.partnerFacilityObject?.address?.uuid;

          const currentFacility = res.items.find(
            (facilityDobryAgent: FacilityDobryAgent) =>
              facilityDobryAgent.asf24AddressUuid === asf24AddressUuid
          );

          if (!currentFacility) {
            this.linkedDobryAgentFacility = null;
            this.facilityDobryAgentControl.setValue('');
            return;
          }

          this.linkedDobryAgentFacility = currentFacility;
          this.facilityDobryAgentControl.setValue(currentFacility);
        },
        error: () => {
          this.checkingDobryAgentLink = false;
          this.linkedDobryAgentFacility = null;
          this.facilityDobryAgentControl.setValue('');
        },
      });
  }

  private hasPartnerTag(facility: PartnerFacility | null | undefined, tagKey: string): boolean {
    if (!facility?.partners?.length) {
      return false;
    }

    return facility.partners.some((partner: Partner) => {
      return partner?.tags?.some((tag: Dictionary) => tag.key === tagKey);
    });
  }

  save(): void {
    this.processingRequest = true;

    this.facilityService.sendForm(
      this.partnerFacilityForm.getRawValue(),
      this.partnerFacilityObject ? this.partnerFacilityObject.address.uuid : null
    )
      .pipe(
        first(),
        switchMap((response: Uuid) => {
          return this.saveDobryAgentAfterFacilitySave(response.uuid).pipe(
            map(() => response)
          );
        })
      )
      .subscribe({
        next: (response: Uuid) => {
          this.snackBar.open(
            this.partnerFacilityObject
              ? 'Pomyślnie zaktualizowano placówkę'
              : 'Pomyślnie utworzono placówkę',
            '',
            {
              duration: 2000,
            }
          );

          this.processingRequest = false;

          if (this.partnerFacilityObject) {
            this.closeModification.emit();
          } else {
            this.router.navigate(['/placowki/szczegoly', response.uuid]);
          }
        },
        error: (error: HttpErrorResponse) => {
          this.processingRequest = false;

          if (error.status === 409 && error.error?.code === 'ASF24_FACILITY_ALREADY_LINKED') {
            this.snackBar.open(
              error.error.message,
              'Zamknij',
              {
                duration: 5000,
              }
            );

            return;
          }

          this.snackBar.open(
            'Podczas przesyłania formularza wystąpił błąd',
            error.status?.toString(),
            {
              duration: 2000,
            }
          );

          if (error.status === 400) {
            this.errors = error.error.error.list;
            this.formHelper.setBackendErrorState(this.partnerFacilityForm, this.errors);
          }
        },
      });
  }

  cancel(): void {
    if (!this.partnerFacilityObject) {
      this.router.navigate(['/placowki']);
    } else {
      this.closeModification.emit();
    }
  }

  copyHeadquartersAddress(index: number): void {
    this.partnerService.get(this.partnersFormArray.value[index])
      .subscribe((partner: Partner) => {
        this.addressForm.reset({
          street: partner.headquartersAddress.street,
          houseNumber: partner.headquartersAddress.houseNumber,
          apartmentNumber: partner.headquartersAddress.apartmentNumber,
          city: partner.headquartersAddress.city,
          zipCode: partner.headquartersAddress.zipCode,
          state: partner.headquartersAddress.state?.id,
          postCity: partner.headquartersAddress.postCity,
          district: partner.headquartersAddress.district,
          commune: partner.headquartersAddress.commune,
        });
      });
  }

  copyCommunicationAddress(index: number): void {
    this.partnerService.get(this.partnersFormArray.value[index])
      .subscribe((partner: Partner) => {
        this.addressForm.reset({
          street: partner.communicationAddress.street,
          houseNumber: partner.communicationAddress.houseNumber,
          apartmentNumber: partner.communicationAddress.apartmentNumber,
          city: partner.communicationAddress.city,
          zipCode: partner.communicationAddress.zipCode,
          state: partner.communicationAddress.state?.id,
          postCity: partner.communicationAddress.postCity,
          district: partner.communicationAddress.district,
          commune: partner.communicationAddress.commune,
        });
      });
  }

  addPartner(partner?: Partner): void {
    this.partnersFormArray.push(this.fb.control(partner?.uuid || '', [Validators.required]));
  }

  removePartner(index: number): void {
    this.partnersFormArray.removeAt(index);
  }

  getPartnerControl(index: number): FormControl {
    return this.partnersFormArray.controls[index] as FormControl;
  }

  private saveDobryAgentAfterFacilitySave(asf24AddressUuid: string): Observable<any> {
    if (!this.dobryAgentFacility) {
      return of(null);
    }
    return this.getSelectedPartnersPayload().pipe(
      switchMap((partners: any[]) => {
        const selectedDobryAgentFacility = this.facilityDobryAgentControl.value;
        const facilityUuid = selectedDobryAgentFacility?.uuid;

        if (this.addFacilityDobryAgentControl.value) {
          const payload = this.prepareDobryAgentFacilityPayload(asf24AddressUuid, partners);

          return this.facilityDobryAgentService.sendForm(payload);
        }

        if (facilityUuid) {
          const payloadLink = {
            facilityUuid,
            asf24AddressUuid,
            partners,
          };

          return this.facilityDobryAgentService.linkFacilityDobryAgent(payloadLink);
        }

        return of(null);
      })
    );
  }

  private getSelectedPartnersPayload(): Observable<any[]> {
    const partnerIds = (this.partnersFormArray.value ?? [])
      .filter((partnerId: string) => !!partnerId);

    if (!partnerIds.length) {
      return of([]);
    }

    return forkJoin(
      partnerIds.map((partnerId: string) =>
        this.partnerService.get(partnerId).pipe(take(1))
      )
    ).pipe(
      map((partners: Partner[]) => {
        return partners.map((partner: any) => ({
          uuid: partner.uuid,
          fullName: partner.fullName,
          shortName: partner.shortName,
          nip: partner.nip,
          email: partner.primaryEmails?.[0]?.address ?? partner.emails?.[0]?.address ?? null,
          phone: partner.primaryPhones?.[0]?.number ?? partner.phones?.[0]?.number ?? null,
        }));
      })
    );
  }

  private prepareDobryAgentFacilityPayload(asf24AddressUuid: string, partners: any[]): any {
    const rawValue = this.partnerFacilityForm.getRawValue();
    const address = rawValue.address ?? {};

    return {
      asf24AddressUuid,

      name: this.facilityDobryAgentNameControl.value,
      seoSlug: this.facilityDobryAgentNameControl.value,
      seoTitle: this.facilityDobryAgentNameControl.value,
      description: this.facilityDobryAgentDescriptionControl.value,
      city: this.facilityDobryAgentCityControl.value,

      address: {
        street: address.street ?? null,
        houseNumber: address.houseNumber ?? null,
        apartmentNumber: address.apartmentNumber ?? null,
        zipCode: address.zipCode ?? null,
        city: address.city ?? null,
        postCity: address.postCity ?? null,
        state: address.state ?? null,
        district: address.district ?? null,
        commune: address.commune ?? null,
      },

      partners,

      active: false,
    };
  }
}
